import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import { FERRAMENTAS, FERRAMENTAS_EVENT } from '../utils/configFerramentas';

/*
 * ConfigsContext
 * Fonte unica de verdade das feature flags por idcliente+idloja.
 *
 * - Carrega TODAS as configs em uma unica chamada GET /v1/configs/all
 *   ao montar e quando idcliente/idloja mudam.
 * - Espelha o resultado em localStorage como cache (compatibilidade com
 *   codigo legado que ainda le localStorage diretamente) e dispara os
 *   eventos custom existentes (configVendasChanged, configPcpChanged, ...).
 * - Re-valida automaticamente quando a janela volta ao foco (polling leve).
 * - Expor refresh() permite que telas de "Salvar" forcem reload imediato.
 */

const DEFAULTS = {
  // ATENCAO: normalize() copia SOMENTE as chaves declaradas aqui. Campo novo
  // que nao entrar nesta lista e descartado silenciosamente, mesmo vindo
  // preenchido de /v1/configs/all.
  vendas: {
    contrato: 0, ordem_servico: 0, pdv: 0, mostrar_sku_pedido: 0,
    exibicao_cliente_pedido: 0, venda_rapida: 0, layout_pedido: 0,
    // Perfil da OS: 0 servicos gerais, 1 oficina e veiculos, 2 instalacao veicular.
    perfil_os: 0,
    // Fechamento do PDV no financeiro (por cliente): 0 nao lanca, 1 por
    // meio de pagamento, 2 total unico. 1 e o comportamento historico.
    pdv_fechamento_financeiro: 1, pdv_credito_a_receber: 1,
    // Textos padrao da proposta (Garantia e Validade da Proposta). Entram em
    // toda proposta NOVA; o vendedor pode trocar na propria proposta.
    // String vazia de proposito: normalize() so converte para numero o que
    // tem default numerico, entao texto precisa nascer como texto aqui.
    proposta_garantia: '', proposta_validade: '',
  },
  pcp: {
    ficha_tecnica: 0,
    ordem_producao: 0,
    controle_lote: 0,
    controle_validade: 0,
    mrp: 0,
    custo_producao: 0,
    rastreabilidade: 0,
    lote_obrigatorio: 0,
    validade_obrigatorio: 0,
  },
  produto: {
    conversao_automatica: 0,
    controle_lote: 0,
    controle_validade: 0,
    controle_orgao: 0,
    tipo_calculo_preco: 'MARGEM',
  },
  expedicao: {
    expedicao_ativa: 0,
    permitir_envio_parcial: 0,
    utilizar_conferente: 0,
  },
  creditoloja: { creditoloja_ativo: 0 },
  fiscal: { origem_tributacao: 'PRODUTO' },
  // Analise de credito (Configuracoes > Financeiro). Modo do pedido:
  // 0 nao confere, 1 avisa, 2 bloqueia. Quem decide e o backend; aqui serve
  // para o menu e para os textos da tela.
  financeiro: { analise_credito_ativo: 0, analise_validade_dias: 180, analise_modo_pedido: 1 },
  // Ferramentas opcionais do menu (Configuracoes > Ferramentas), por loja.
  // Defaults vem do campo `padrao` de configFerramentas.js; o backend so
  // devolve o que a loja gravou.
  ferramentas: FERRAMENTAS.reduce((acc, f) => ({ ...acc, [f.key]: f.padrao ? 1 : 0 }), {}),
};

// Mapeia secao -> { storageKey, eventName, transform(section -> stored) }
// transform existe pq o codigo legado armazena formatos especificos
// (ex: configFiscalOrigemTributacao guarda apenas a string 'PRODUTO').
const STORAGE_MAP = {
  vendas: {
    storageKey: 'configVendas',
    eventName: 'configVendasChanged',
    transform: (s) => JSON.stringify(s),
  },
  pcp: {
    storageKey: 'configPcp',
    eventName: 'configPcpChanged',
    transform: (s) => JSON.stringify(s),
  },
  produto: {
    storageKey: 'configProduto',
    eventName: 'configProdutoChanged',
    transform: (s) => JSON.stringify(s),
  },
  expedicao: {
    storageKey: 'configExpedicao',
    eventName: 'configExpedicaoChanged',
    transform: (s) => JSON.stringify(s),
  },
  creditoloja: {
    storageKey: 'configCreditoLoja',
    eventName: 'configCreditoLojaChanged',
    transform: (s) => JSON.stringify(s),
  },
  financeiro: {
    storageKey: 'configFinanceiro',
    eventName: 'configFinanceiroChanged',
    transform: (s) => JSON.stringify(s),
  },
  ferramentas: {
    // Mesmo cache que configFerramentas.js le (Sidebar, FerramentaGuard),
    // em booleanos como o formato antigo.
    storageKey: 'configFerramentas',
    eventName: FERRAMENTAS_EVENT,
    transform: (s) => JSON.stringify(
      Object.keys(s).reduce((acc, k) => ({ ...acc, [k]: !!s[k] }), {}),
    ),
  },
  fiscal: {
    storageKey: 'configFiscalOrigemTributacao',
    eventName: 'configFiscalChanged',
    // valor primitivo para esse caso (codigo legado le como string crua)
    transform: (s) => String(s.origem_tributacao || 'PRODUTO'),
  },
};

// Aceita tanto snake_case quanto camelCase (DataSet.Serialize do Delphi varia)
const readVal = (obj, snake, fallback = 0) => {
  if (!obj || typeof obj !== 'object') return fallback;
  if (obj[snake] !== undefined) return obj[snake];
  const camel = snake.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  if (obj[camel] !== undefined) return obj[camel];
  return fallback;
};

const normalize = (data) => {
  if (!data || typeof data !== 'object') return DEFAULTS;
  const sectionFor = (key, defaults) => {
    const src = data[key] || data[key.toUpperCase()] || {};
    const out = {};
    Object.keys(defaults).forEach((k) => {
      const raw = readVal(src, k, defaults[k]);
      // Numericos viram numero; strings ficam strings
      if (typeof defaults[k] === 'number') {
        out[k] = Number(raw) || 0;
      } else {
        out[k] = raw == null ? defaults[k] : String(raw);
      }
    });
    return out;
  };
  return {
    vendas: sectionFor('vendas', DEFAULTS.vendas),
    pcp: sectionFor('pcp', DEFAULTS.pcp),
    produto: sectionFor('produto', DEFAULTS.produto),
    expedicao: sectionFor('expedicao', DEFAULTS.expedicao),
    creditoloja: sectionFor('creditoloja', DEFAULTS.creditoloja),
    fiscal: sectionFor('fiscal', DEFAULTS.fiscal),
    financeiro: sectionFor('financeiro', DEFAULTS.financeiro),
    ferramentas: sectionFor('ferramentas', DEFAULTS.ferramentas),
  };
};

const writeCache = (configs) => {
  Object.keys(STORAGE_MAP).forEach((secao) => {
    try {
      const { storageKey, eventName, transform } = STORAGE_MAP[secao];
      const next = transform(configs[secao] || DEFAULTS[secao]);
      const current = localStorage.getItem(storageKey);
      if (current !== next) {
        localStorage.setItem(storageKey, next);
        try {
          window.dispatchEvent(new Event(eventName));
        } catch (e) { /* IE-safe noop */ }
      }
    } catch (e) {
      // sem cache; em memoria ainda funciona
    }
  });
};

const readCache = () => {
  // Reconstroi configs a partir do cache (usado como bootstrap antes da resposta da API)
  const out = {
    vendas: { ...DEFAULTS.vendas },
    pcp: { ...DEFAULTS.pcp },
    produto: { ...DEFAULTS.produto },
    expedicao: { ...DEFAULTS.expedicao },
    creditoloja: { ...DEFAULTS.creditoloja },
    fiscal: { ...DEFAULTS.fiscal },
    financeiro: { ...DEFAULTS.financeiro },
    ferramentas: { ...DEFAULTS.ferramentas },
  };
  try {
    const v = localStorage.getItem('configFerramentas');
    if (v) {
      const salvo = JSON.parse(v);
      Object.keys(salvo).forEach((k) => { out.ferramentas[k] = salvo[k] ? 1 : 0; });
    }
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configVendas');
    if (v) Object.assign(out.vendas, JSON.parse(v));
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configPcp');
    if (v) Object.assign(out.pcp, JSON.parse(v));
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configProduto');
    if (v) Object.assign(out.produto, JSON.parse(v));
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configExpedicao');
    if (v) Object.assign(out.expedicao, JSON.parse(v));
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configCreditoLoja');
    if (v) Object.assign(out.creditoloja, JSON.parse(v));
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configFinanceiro');
    if (v) Object.assign(out.financeiro, JSON.parse(v));
  } catch (e) { /* mantem default */ }
  try {
    const v = localStorage.getItem('configFiscalOrigemTributacao');
    if (v) out.fiscal.origem_tributacao = v;
  } catch (e) { /* mantem default */ }
  return out;
};

// Migracao unica das ferramentas: antes de existir gesconfig_ferramenta, o
// liga/desliga ficava so no navegador. Se a loja ainda nao tem nada gravado e
// este navegador tem escolhas antigas, sobe essas escolhas para a loja -- quem
// ja tinha ligado Integracoes/Alertas nao perde a tela no deploy.
// A marca 'configFerramentasMigrado' (prefixo config*, limpa no logout) impede
// que, ao trocar de loja, o cache de uma loja seja copiado para outra.
const MARCA_MIGRACAO = 'configFerramentasMigrado';

const marcarMigrado = () => {
  try { localStorage.setItem(MARCA_MIGRACAO, '1'); } catch (e) { /* noop */ }
};

const migrarFerramentasDoNavegador = async (secaoServidor, idcliente, idloja) => {
  let jaMigrado = false;
  let local = null;
  try {
    jaMigrado = localStorage.getItem(MARCA_MIGRACAO) === '1';
    local = JSON.parse(localStorage.getItem('configFerramentas') || 'null');
  } catch (e) { /* sem cache legivel: nada a migrar */ }
  if (jaMigrado) return null;

  // Loja ja tem escolhas gravadas: o servidor manda, nada a subir.
  if (Object.keys(secaoServidor).length > 0 || !local || typeof local !== 'object') {
    marcarMigrado();
    return null;
  }

  // So o que difere do default (o resto continua sem linha = default).
  const escolhas = Object.keys(DEFAULTS.ferramentas)
    .filter((k) => k in local && (local[k] ? 1 : 0) !== DEFAULTS.ferramentas[k]);
  if (escolhas.length === 0) {
    marcarMigrado();
    return null;
  }

  const gravadas = {};
  let falhou = false;
  await Promise.all(escolhas.map((chave) => api
    .put('/v1/configs/ferramentas', { idcliente, idloja, chave, ativo: !!local[chave] })
    .then(() => { gravadas[chave] = local[chave] ? 1 : 0; })
    .catch(() => { falhou = true; })));
  // Falha (ex.: migration 0038 ainda nao aplicada): tenta de novo no proximo
  // carregamento. Enquanto isso o cache local continua valendo.
  if (falhou) return { ...local, ...gravadas };
  marcarMigrado();
  return gravadas;
};

const ConfigsContext = createContext(null);

const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000; // 5 min entre revalidacoes ao focar

export function ConfigsProvider({ children }) {
  // Bootstrap a partir do cache para evitar "flash de menu vazio"
  const [configs, setConfigs] = useState(() => readCache());
  const [loaded, setLoaded] = useState(false); // true apos primeira resposta da API
  const [error, setError] = useState(null);
  const lastFetchAt = useRef(0);
  const inflight = useRef(null);

  const getSession = () => {
    const idcliente = parseInt(localStorage.getItem('sessionCodidcliente'), 10) || 0;
    const idloja = parseInt(localStorage.getItem('sessionloja'), 10) || 0;
    const isLogged = JSON.parse(localStorage.getItem('isLogged') || 'false');
    const token = localStorage.getItem('sessionToken');
    return { idcliente, idloja, isLogged, token };
  };

  const fetchConfigs = useCallback(async () => {
    const { idcliente, idloja, isLogged, token } = getSession();
    if (!isLogged || !token || !idcliente || !idloja) {
      // Sem sessao valida: apenas marca como "loaded" para liberar guards
      // que aguardam — eles ja vao redirecionar pelo proprio fluxo de auth.
      setLoaded(true);
      return;
    }
    // Dedup: se ja ha uma requisicao em curso, aguarda a mesma
    if (inflight.current) {
      await inflight.current;
      return;
    }
    inflight.current = (async () => {
      try {
        setError(null);
        // 1) Tenta o agregador (preferencial — 1 chamada, baixa latencia)
        const { data } = await api.get('/v1/configs/all', {
          params: { idcliente, idloja },
        });
        // Ferramentas: so confia no servidor quando ele ja manda a secao
        // (backend novo). Backend antigo -> mantem o cache do navegador em
        // vez de voltar tudo ao default. Migracao roda antes do writeCache,
        // que sobrescreve o cache antigo.
        let dataFinal = data;
        if (data && typeof data.ferramentas === 'object' && data.ferramentas !== null) {
          const migradas = await migrarFerramentasDoNavegador(data.ferramentas, idcliente, idloja);
          if (migradas) {
            dataFinal = { ...data, ferramentas: { ...data.ferramentas, ...migradas } };
          }
        } else {
          dataFinal = { ...data, ferramentas: readCache().ferramentas };
        }
        const normalized = normalize(dataFinal);
        setConfigs(normalized);
        writeCache(normalized);
        lastFetchAt.current = Date.now();
        setLoaded(true);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          // Logout sera tratado pelo interceptor de api.js
          setLoaded(true);
          return;
        }
        // 2) Fallback: agregador indisponivel (404, 500 ou rede). Busca dos
        //    endpoints individuais para garantir que as configs aparecam
        //    mesmo durante janela de deploy ou se /v1/configs/all nao existir.
        try {
          const params = { idcliente, idloja };
          const settled = await Promise.allSettled([
            api.get('/v1/config-vendas',      { params }).then((r) => ['vendas',     r.data]),
            api.get('/v1/config-pcp',         { params }).then((r) => ['pcp',        r.data]),
            api.get('/v1/config-produto',     { params }).then((r) => ['produto',    r.data]),
            api.get('/v1/config-expedicao',   { params }).then((r) => ['expedicao',  r.data]),
            api.get('/v1/config-creditoloja', { params }).then((r) => ['creditoloja', r.data]),
            api.get('/v1/config-fiscal',      { params }).then((r) => ['fiscal',     r.data]),
            api.get('/v1/config-financeiro',  { params }).then((r) => ['financeiro', r.data]),
          ]);
          const aggregated = {};
          settled.forEach((res) => {
            if (res.status === 'fulfilled') {
              const [secao, payload] = res.value;
              aggregated[secao] = payload;
            }
          });
          // Ferramentas nao tem endpoint individual: no fallback mantem o
          // que ja estava em cache em vez de voltar tudo ao default.
          aggregated.ferramentas = readCache().ferramentas;
          const normalized = normalize(aggregated);
          setConfigs(normalized);
          writeCache(normalized);
          lastFetchAt.current = Date.now();
          setError(null);
        } catch (fallbackErr) {
          // Falha total: mantem cache (localStorage) em uso, libera guards.
          setError(err?.message || 'Erro ao carregar configuracoes');
        } finally {
          setLoaded(true);
        }
      } finally {
        inflight.current = null;
      }
    })();
    await inflight.current;
  }, []);

  // Carrega na montagem e quando sessao muda (storage event de outras abas)
  useEffect(() => {
    fetchConfigs();
    const onStorage = (e) => {
      if (e.key === 'sessionloja' || e.key === 'sessionCodidcliente' || e.key === 'sessionToken') {
        // Sessao mudou em outra aba — recarrega
        fetchConfigs();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchConfigs]);

  // Polling leve: revalida ao voltar foco da janela, respeitando intervalo minimo
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastFetchAt.current >= REVALIDATE_INTERVAL_MS) {
        fetchConfigs();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onFocus();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchConfigs]);

  // refresh forcado (chamado por telas de Salvar para refletir DB ja na hora)
  const refresh = useCallback(async () => {
    lastFetchAt.current = 0;
    inflight.current = null;
    await fetchConfigs();
  }, [fetchConfigs]);

  const value = {
    loaded,
    error,
    configs,
    refresh,
  };

  return (
    <ConfigsContext.Provider value={value}>{children}</ConfigsContext.Provider>
  );
}

ConfigsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useConfigs() {
  const ctx = useContext(ConfigsContext);
  if (!ctx) {
    // Permite uso fora do provider (modo defensivo) — retorna defaults sem quebrar
    return {
      loaded: false,
      error: null,
      configs: readCache(),
      refresh: async () => {},
    };
  }
  return ctx;
}

export default ConfigsContext;
